/**
 * S1 (AB#2204, SG-05) + S2 (AB#2205) — a staff member's own booking in the
 * shared booking detail, opened from the Appointments list, My Day or a
 * notification. Data comes from the self-scoped staff endpoint (masked phone,
 * backend `allowedTransitions` / `canMarkPaid`); a list row, when there is one,
 * fills the dialog until the detail answers. No owner-only controls.
 */
import React, { useRef, useState } from 'react';
import { useTranslation as useTranslationUntyped } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import { useStaffAppointmentDetail } from '../../hooks/useStaffAppointmentDetail';
import type { AppointmentListItem, SalonOwnerAppointmentRowDto } from '../../services/appointmentsList';
import type { StaffAppointmentDetailDto } from '../../types/api-responses';
import MarkAsPaidModal from '../MarkAsPaidModal/MarkAsPaidModal';
import BookingDetailDialog, { type BookingDetailModel } from './BookingDetailDialog';
import s from './BookingDetail.module.css';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useTranslation = useTranslationUntyped as any;

export function addMinutesHHmm(hhmm: string, minutes: number): string {
    const [h, m] = hhmm.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
    const total = (h * 60 + m + minutes) % (24 * 60);
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** O1 (AB#2199) — a list row (owner or staff) → the shared booking detail model. */
export function listBookingModel(item: AppointmentListItem): BookingDetailModel {
    const row = item as Partial<SalonOwnerAppointmentRowDto> & AppointmentListItem;
    return {
        id: row.id,
        serviceName: row.serviceName,
        status: row.status,
        cancellationSource: row.cancellationSource,
        allowedTransitions: row.allowedTransitions,
        canMarkPaid: row.canMarkPaid,
        paymentStatus: row.paymentStatus,
        date: row.appointmentDate.slice(0, 10),
        startTime: row.branchLocalStartTime,
        endTime: addMinutesHHmm(row.branchLocalStartTime, row.durationMinutes),
        customerName: row.clientDisplayName,
        customerPhone: row.clientPhoneFull || row.clientPhoneMasked,
        staffName: row.staffDisplayName,
        branchName: row.branchName,
        amount: row.amount,
        currencyIso: row.currencyIso,
        bundleName: row.bundleName,
        isBundle: row.bundleBookingId != null,
    };
}

/**
 * S1 — a staff member's own booking: the list row (when there is one), refreshed
 * with the staff detail endpoint once it answers. Only the fields the staff
 * endpoint returns are used — never owner-only data.
 */
export function staffBookingModel(
    row: AppointmentListItem | undefined,
    detail: StaffAppointmentDetailDto | undefined,
    language: string,
): BookingDetailModel {
    const base: BookingDetailModel = row
        ? listBookingModel(row)
        : { id: detail?.id ?? 0, serviceName: '', status: '', startTime: '' };
    if (!detail) return base;
    const start = detail.branchLocalStartTime || base.startTime;
    const endTime = detail.endTime
        || (detail.branchLocalStartTime && detail.durationMinutes != null
            ? addMinutesHHmm(detail.branchLocalStartTime, detail.durationMinutes)
            : base.endTime);
    return {
        ...base,
        serviceName: (language === 'ar' && detail.serviceNameAr) || detail.serviceName || base.serviceName,
        status: detail.status ?? base.status,
        cancellationSource: detail.cancellationSource !== undefined ? detail.cancellationSource : base.cancellationSource,
        allowedTransitions: detail.allowedTransitions ?? base.allowedTransitions,
        canMarkPaid: detail.canMarkPaid ?? base.canMarkPaid,
        paymentStatus: detail.paymentStatus ?? base.paymentStatus,
        date: detail.appointmentDate ? detail.appointmentDate.slice(0, 10) : base.date,
        startTime: start,
        endTime,
        customerName: detail.clientDisplayName ?? base.customerName,
        customerPhone: detail.clientPhoneMasked ?? base.customerPhone,
        staffName: detail.staffDisplayName ?? base.staffName,
        branchName: (language === 'ar' && detail.branchNameAr) || detail.branchName || base.branchName,
        amount: detail.amount ?? base.amount,
        currencyIso: detail.currencyIso ?? base.currencyIso,
        notes: detail.notes ?? base.notes,
    };
}

/** Generate a UUID v4 idempotency key (mark-paid). */
export function generateIdempotencyKey(): string {
    if (typeof window.crypto?.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
    });
}

/** A small dialog while the booking loads, or when the staff member can't open it. */
const StaffBookingPlaceholder: React.FC<{ message: string; busy: boolean; onClose: () => void }> = ({ message, busy, onClose }) => {
    const { t } = useLanguage();
    return (
        <div className={s.backdrop} role="presentation" onClick={onClose}>
            <div
                className={s.dialog}
                role="dialog"
                aria-modal="true"
                aria-label={t('booking.detail.title')}
                aria-busy={busy || undefined}
                data-testid="booking-detail-placeholder"
                onClick={e => e.stopPropagation()}
                onKeyDown={e => { if (e.key === 'Escape') onClose(); }}
            >
                <div className={s.body}>
                    <p className={s.reason} role={busy ? 'status' : 'alert'}>{message}</p>
                </div>
                <div className={s.footer}>
                    <button type="button" className={s.ghostBtn} onClick={onClose} autoFocus>
                        {t('calendar.peek.close')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export interface StaffBookingDetailProps {
    appointmentId: number;
    /** The list row when the booking is on screen; optional (My Day, notifications). */
    row?: AppointmentListItem;
    onClose: () => void;
    onMarkPaid: (booking: BookingDetailModel) => void;
}

/** S1 — the shared booking detail for a staff member's own booking. */
export const StaffBookingDetail: React.FC<StaffBookingDetailProps> = ({ appointmentId, row, onClose, onMarkPaid }) => {
    const { t, language } = useLanguage();
    const { data, isError } = useStaffAppointmentDetail(appointmentId);
    if (!row && !data) {
        return (
            <StaffBookingPlaceholder
                busy={!isError}
                message={isError ? t('stf.detail.unavailable') : t('stf.loading')}
                onClose={onClose}
            />
        );
    }
    const booking = staffBookingModel(row, data, language);
    return (
        <BookingDetailDialog
            booking={booking}
            actor="staff"
            onClose={onClose}
            onMarkPaid={() => onMarkPaid(booking)}
            notice={isError ? t('stf.detail.loadFailed') : null}
        />
    );
};

/**
 * S2 — detail + "Mark as paid" in one place, for surfaces that don't own a
 * mark-paid modal (My Day, notifications, an Appointments deep link).
 */
export const StaffBookingFlow: React.FC<{ appointmentId: number; row?: AppointmentListItem; onClose: () => void }> = ({
    appointmentId, row, onClose,
}) => {
    const { t } = useTranslation('appointments');
    const { showToast } = useToast();
    const [payTarget, setPayTarget] = useState<BookingDetailModel | null>(null);
    const keyRef = useRef('');

    if (payTarget) {
        return (
            <MarkAsPaidModal
                appointmentId={payTarget.id}
                customer={{ name: payTarget.customerName ?? '' }}
                service={{ name: payTarget.serviceName, amount: payTarget.amount ?? 0, currency: payTarget.currencyIso ?? '' }}
                staff={{ name: payTarget.staffName ?? '', branch: payTarget.branchName ?? '' }}
                isOpen
                onClose={onClose}
                onSuccess={() => showToast(t('markPaidModal.success'), 'success')}
                idempotencyKey={keyRef.current}
                source="list"
                bundle={payTarget.isBundle ? { name: payTarget.bundleName } : undefined}
            />
        );
    }
    return (
        <StaffBookingDetail
            appointmentId={appointmentId}
            row={row}
            onClose={onClose}
            onMarkPaid={booking => { keyRef.current = generateIdempotencyKey(); setPayTarget(booking); }}
        />
    );
};
